import { useRef, useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { Vector3 } from 'three'
import { getMediaQueryKey } from '../../utils/deviceDetection'

/**
 * MobilePortraitLaunchCamera
 * Smoothly transitions the camera to the rocket on activation, then keeps the
 * orbit target locked onto the rocket so the user can freely rotate around it
 * using standard CameraControls (drei).
 *
 * Single-system approach: on activation we fire one setLookAt (spring toward
 * rocket), then immediately run delta tracking every frame from frame 1.
 * CameraControls composes both simultaneously — no Phase 1/Phase 2 handoff,
 * no seam, one continuous motion.
 */
export function MobilePortraitLaunchCamera({
  isActive = false,
  followModeActive = false,
  artemisPositionRef = null,  // ref to [x,y,z] — updated imperatively, no re-renders
  cameraOffset = [2, 3, 4],
  lookAtOffset = [0, 0, 0],
  smoothTime = 0.5,
  returnPosition = [0, 5, 30],
  returnLookAt = [0, 0, 0],
}) {
  const wasActive = useRef(false)
  const isReturning = useRef(false)
  const returnTimerRef = useRef(null)

  // Reusable vectors
  const targetPosition = useRef(new Vector3())
  const lookAtPosition = useRef(new Vector3())
  const currentArtemisPos = useRef(new Vector3())
  const prevArtemisPos = useRef(new Vector3())

  // Cleanup return timer on unmount
  useEffect(() => {
    return () => {
      if (returnTimerRef.current) {
        clearTimeout(returnTimerRef.current)
        window.followCameraReturning = false
      }
    }
  }, [])

  useFrame(() => {
    const mediaQueryKey = getMediaQueryKey()
    const isMobilePortrait = mediaQueryKey === 'mobilePortrait'

    const shouldActivate = isActive && (isMobilePortrait || followModeActive)

    const controls = window.orbitControlsRef
    if (!controls) return

    // Update current Artemis position from ref
    const ap = artemisPositionRef?.current
    currentArtemisPos.current.set(
      ap ? ap[0] : 0,
      ap ? ap[1] : 0,
      ap ? ap[2] : 0
    )

    // Camera eye position = rocket + offset
    targetPosition.current.set(
      currentArtemisPos.current.x + cameraOffset[0],
      currentArtemisPos.current.y + cameraOffset[1],
      currentArtemisPos.current.z + cameraOffset[2]
    )

    // LookAt = rocket + optional offset
    lookAtPosition.current.set(
      currentArtemisPos.current.x + lookAtOffset[0],
      currentArtemisPos.current.y + lookAtOffset[1],
      currentArtemisPos.current.z + lookAtOffset[2]
    )

    // ---------------------------------------------------------------
    // DEACTIVATION — fire return animation once, hand smoothTime back
    // to Camera.jsx via window.followCameraReturning flag
    // ---------------------------------------------------------------
    if (!shouldActivate && wasActive.current && !isReturning.current) {
      isReturning.current = true
      wasActive.current = false

      // Shortest-path azimuth so we never spin more than 180°
      if (controls.azimuthAngle !== undefined) {
        const rdx = returnPosition[0] - returnLookAt[0]
        const rdz = returnPosition[2] - returnLookAt[2]
        const destAzimuth = Math.atan2(rdx, rdz)
        let diff = destAzimuth - controls.azimuthAngle
        while (diff > Math.PI) diff -= Math.PI * 2
        while (diff < -Math.PI) diff += Math.PI * 2
        controls.azimuthAngle = destAzimuth - diff
      }

      // Take ownership of smoothTime for the return spring duration
      window.followCameraReturning = true
      controls.smoothTime = smoothTime
      controls.setLookAt(
        returnPosition[0], returnPosition[1], returnPosition[2],
        returnLookAt[0], returnLookAt[1], returnLookAt[2],
        true
      )

      // Release ownership once the spring has had time to settle
      if (returnTimerRef.current) clearTimeout(returnTimerRef.current)
      returnTimerRef.current = setTimeout(() => {
        window.followCameraReturning = false
        isReturning.current = false
        returnTimerRef.current = null
      }, smoothTime * 4000) // 4× smoothTime in ms gives full spring settle

      return
    }

    // While returning, do nothing — Camera.jsx spring handles it
    if (!shouldActivate) return

    // ---------------------------------------------------------------
    // ACTIVATION — on first frame, fire initial setLookAt toward the
    // rocket (starts the spring), then fall through to delta tracking
    // immediately. CameraControls composes both simultaneously.
    // ---------------------------------------------------------------
    if (!wasActive.current) {
      wasActive.current = true
      isReturning.current = false

      // Cancel any pending return timer
      if (returnTimerRef.current) {
        clearTimeout(returnTimerRef.current)
        returnTimerRef.current = null
        window.followCameraReturning = false
      }

      // Shortest-path azimuth toward the rocket
      if (controls.azimuthAngle !== undefined) {
        const tdx = targetPosition.current.x - lookAtPosition.current.x
        const tdz = targetPosition.current.z - lookAtPosition.current.z
        const destAzimuth = Math.atan2(tdx, tdz)
        let d = destAzimuth - controls.azimuthAngle
        while (d > Math.PI) d -= Math.PI * 2
        while (d < -Math.PI) d += Math.PI * 2
        controls.azimuthAngle = destAzimuth - d
      }

      // One-shot spring toward the rocket — Camera.jsx is blocked from
      // overriding smoothTime while followModeActive is true
      controls.smoothTime = smoothTime
      controls.setLookAt(
        targetPosition.current.x,
        targetPosition.current.y,
        targetPosition.current.z,
        lookAtPosition.current.x,
        lookAtPosition.current.y,
        lookAtPosition.current.z,
        true
      )

      prevArtemisPos.current.copy(currentArtemisPos.current)
    }

    // ---------------------------------------------------------------
    // EVERY ACTIVE FRAME — delta tracking keeps the orbit target on
    // the rocket as it moves. Works from frame 1 alongside the spring,
    // so there is no Phase 1/Phase 2 handoff — one continuous motion.
    // ---------------------------------------------------------------
    const dx = currentArtemisPos.current.x - prevArtemisPos.current.x
    const dy = currentArtemisPos.current.y - prevArtemisPos.current.y
    const dz = currentArtemisPos.current.z - prevArtemisPos.current.z

    if (Math.abs(dx) > 0.00001 || Math.abs(dy) > 0.00001 || Math.abs(dz) > 0.00001) {
      const currentTarget = new Vector3()
      controls.getTarget(currentTarget)
      controls.moveTo(
        currentTarget.x + dx,
        currentTarget.y + dy,
        currentTarget.z + dz,
        true
      )
    }

    prevArtemisPos.current.copy(currentArtemisPos.current)
  })

  return null
}

export default MobilePortraitLaunchCamera
